import { UPLOAD_STATUS } from '@/shared/config';
import { Chip, CustomButton } from '@/shared/ui/components';
import { getDuration } from '@/shared/lib';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import { Modal, Progress, Steps } from 'antd';
import _ from 'lodash';
import { t } from 'ttag';
import { useUploadStatusModal } from './useUploadStatusModal';
import './uploadStatusModal.scss';

export interface IProps {
  isOpen: boolean;
  rightButtonName: string;
  onClickRightButton: () => void;
  study: any;
}

interface DataUpload {
  title: string;
  status: string;
  aboutTimeLeft: number;
}

/**
 * Shows the ECG upload status of a study as a vertical step list above its overall progress. Percentage, aboutTimeLeftTotal, currentStep and dataUpload come from useUploadStatusModal, the Steps items are rebuilt from renderTitle and renderDescription whenever dataUpload changes, and the body prints the total uploaded percentage with the getDuration time left, the progress bar and a single acknowledge button.
 *
 * @param isOpen - Whether the modal is visible.
 * @param rightButtonName - Label of the acknowledge button.
 * @param onClickRightButton - Callback closing the modal.
 * @param study - The study whose upload steps are shown.
 * @returns The upload status modal element.
 */
const UploadStatusModal = ({ isOpen, rightButtonName, onClickRightButton, study }: IProps) => {
  const { state, setState } = useUploadStatusModal({ study });

  /**
   * Renders the heading of one ECG upload step. Prints the step title beside a non-closable Chip coloured green for UPLOADED, blue for UPLOADING and grey otherwise, labelled with the step status.
   *
   * @param dataUpload - The upload step whose title and status are rendered.
   * @returns The step title and status chip elements.
   */
  const renderTitle = (dataUpload: DataUpload) => (
    <>
      <span>{dataUpload.title}</span>
      <Chip
        background={
          dataUpload.status === UPLOAD_STATUS.UPLOADED
            ? '#EDFAF0'
            : dataUpload.status === UPLOAD_STATUS.UPLOADING
              ? '#EBF8FF'
              : '#EAEDF1'
        }
        color={
          dataUpload.status === UPLOAD_STATUS.UPLOADED
            ? '#07A83D'
            : dataUpload.status === UPLOAD_STATUS.UPLOADING
              ? '#005FB2'
              : '#444F66'
        }
        closable={false}
        className="custom-chip"
        borderColor="transparent"
      >
        {dataUpload.status}
      </Chip>
    </>
  );

  /**
   * Renders the secondary line of one ECG upload step. The remaining time formatted by getDuration from aboutTimeLeft in seconds is shown only while the step is UPLOADING, and null for every other status.
   *
   * @param uploadData - The upload step whose remaining time is rendered.
   * @returns The description element, or null when the step is not uploading.
   */
  const renderDescription = (uploadData: DataUpload) => {
    if (uploadData.status === UPLOAD_STATUS.UPLOADING) {
      return (
        <div className="description">
          {`About ${getDuration(uploadData.aboutTimeLeft, 'd[d] hh[h] mm[m]', 'h', 'seconds')} left`}
        </div>
      );
    }
    return null;
  };

  useUpdateEffect(() => {
    /**
     * Holds the Steps items describing each ECG upload stage. Maps every entry of state.dataUpload to a title from renderTitle paired with a description from renderDescription.
     *
     * @returns The rendered step descriptors for the modal.
     */
    const dataRender = _.map(state.dataUpload, (item) => ({
      title: renderTitle(item),
      description: renderDescription(item),
    }));
    setState({ dataRender });
  }, [state.dataUpload]);

  return (
    <Modal
      className="confirm-modal warning-modal upload-status-modal"
      open={isOpen}
      centered
      footer={null}
      closeIcon
      maskClosable={false}
      onCancel={onClickRightButton}
      destroyOnClose={false}
      zIndex={1000}
      title={t`Upload status`}
      classNames={{ header: 'header' }}
    >
      <div className="custom-modal-content">
        <div className="body">
          <div className="remaining-time">
            <span className="time-progress">{`${t`Total uploaded`}: ${state.percentage}%`}</span>
            <span>
              {`${t`About`} ${getDuration(state.aboutTimeLeftTotal, 'd[d] hh[h] mm[m]', 'h', 'seconds')} ${t`left`}`}
            </span>
          </div>
          <Progress
            className="study-process"
            percent={state.percentage}
            showInfo={false}
            strokeColor="#007DD6"
            aria-label={t`Upload progress`}
          />
          <Steps
            progressDot
            current={state.currentStep}
            direction="vertical"
            items={state.dataRender}
          />
        </div>
      </div>
      <div className="footer">
        <CustomButton id="right-id-btn" styles={{ minWidth: 80 }} onClick={onClickRightButton}>
          {rightButtonName}
        </CustomButton>
      </div>
    </Modal>
  );
};

export default UploadStatusModal;
